import React from 'react';
import { Box, Typography, SxProps, Theme } from '@mui/material';
import { InfoTooltip } from '../InfoTooltip';

interface FormFieldProps {
  /** Label text to display */
  label: string;
  /** Optional tooltip text to show on info icon */
  tooltip?: string;
  /** Mark field as required with asterisk */
  required?: boolean;
  /** Optional children - when provided, wraps the field. When omitted, renders label only */
  children?: React.ReactNode;
  /** Custom sx props for the container */
  sx?: SxProps<Theme>;
  /** Custom sx props for the label container */
  labelSx?: SxProps<Theme>;
}

/**
 * Reusable form field component with label and optional tooltip.
 * Can be used in two modes:
 * 1. With children: Wraps form controls (TextField, Select, etc.)
 * 2. Without children: Renders just the label (useful with Controller or custom layouts)
 */
export const FormField: React.FC<FormFieldProps> = ({ label, tooltip, required = false, children, sx, labelSx }) => {
  const labelContent = (
    <Box sx={{ display: 'flex', alignItems: 'center', mb: children ? 1 : 0.5, ...labelSx }}>
      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem', color: 'text.primary' }}>
        {label}
        {required && <span style={{ color: '#d32f2f' }}> *</span>}
      </Typography>
      {tooltip && (
        <InfoTooltip fontSize="16px" sx={{ height: 16, color: 'text.secondary' }} title={tooltip} size="small" />
      )}
    </Box>
  );

  // If no children, return just the label (FieldLabel mode)
  if (!children) {
    return labelContent;
  }

  // Otherwise, wrap children (full FormField mode)
  return (
    <Box sx={{ width: '100%', ...sx }}>
      {labelContent}
      {children}
    </Box>
  );
};

export default FormField;
